import { dimensions } from '@factorialco/deprecated-design-system'
import * as d3Hierarchy from 'd3-hierarchy'
import { GetJobPositionsDocument } from 'generated/resources/graphql'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'

import { useAtsEnabled } from 'modules/ats/lib/atsEnabled'
import type { GraphQLEmployee } from 'modules/org_chart/lib/employeeUtils'
import {
  buildDescendantCountByEmployeeId,
  buildDirectReportsByManagerId,
  buildPositionNodesByManagerId,
  type ManagedPositionLike,
} from 'modules/org_chart/lib/treeIndexes'
import { NODE_SIZE } from 'modules/org_chart/components/Node/constants'
import { findTreeNodeById } from 'modules/org_chart/lib/tree'
import type { Coordinates } from 'modules/org_chart/models/Coordinates'
import type {
  AddPositionNodeData,
  EmployeeNodeData,
  NodeData,
  NodeDataTypes,
  PositionNodeData,
} from 'modules/org_chart/models/NodeData'
import type { PointNode } from 'modules/org_chart/models/PointNode'

const queryContext = {
  suspense: false,
}

interface ReturnType {
  tree: PointNode[]
  findNodeByEmployeeId: (id: string) => PointNode | void
  traceTreePathFromEmployee: (employee: GraphQLEmployee) => string[]
  refetch: () => void
  build: (
    heads: GraphQLEmployee[],
    employees: GraphQLEmployee[],
    uncollapsedIds: Set<string>
  ) => PointNode<NodeDataTypes>[]
  initialCoordinates: Coordinates
}

const EMPTY_JOB_POSITIONS: ManagedPositionLike[] = []

const useTree = (
  heads: GraphQLEmployee[],
  employees: GraphQLEmployee[],
  uncollapsedIds: Set<string>,
  width: number,
  canManagePositions: boolean,
  canSeeJobPostings: boolean,
  canCreateJobPostings: boolean,
  allEmployees: GraphQLEmployee[],
  employeeMap: Map<string, GraphQLEmployee>
): ReturnType => {
  const { canRequestUpsell, employee: currentEmployee } = useCurrentSession()
  const { atsEnabled } = useAtsEnabled()
  const canSeePositions = atsEnabled && canManagePositions && canSeeJobPostings
  const {
    data: jobPositionsData,
    loading: fetchingJobPositions,
    refetch,
  } = useQuery(GetJobPositionsDocument, {
    context: queryContext,
    skip: !canSeePositions,
  })
  const jobPositions = jobPositionsData?.positions?.positions ?? EMPTY_JOB_POSITIONS

  const directReportsByManagerId = useUnsafeMemo(() => {
    return buildDirectReportsByManagerId(allEmployees)
  }, [allEmployees])

  const descendantCountByEmployeeId = useUnsafeMemo(() => {
    return buildDescendantCountByEmployeeId(allEmployees, directReportsByManagerId)
  }, [allEmployees, directReportsByManagerId])

  const positionNodesByManagerId = useUnsafeMemo(() => {
    if (!canSeePositions) {
      return new Map<string, PositionNodeData[]>()
    }

    return buildPositionNodesByManagerId(jobPositions)
  }, [canSeePositions, jobPositions])

  const initialCoordinates = useUnsafeMemo<Coordinates>(() => {
    const [nodeWidth, nodeHeight] = NODE_SIZE

    const initialX = width / 2 - nodeWidth / 2
    const initialY = nodeHeight / 1.2
    return [initialX, initialY]
  }, [width])

  const buildTree = useUnsafeCallback(
    (data: { children: NodeData[] }) => {
      const [nodeWidth, nodeHeight] = NODE_SIZE
      const hierarchy = d3Hierarchy.hierarchy(data)
      const treeLayout = d3Hierarchy
        .tree()
        .nodeSize([nodeWidth, nodeHeight + parseInt(dimensions.s80)])
      // eslint-disable-next-line no-type-assertion/no-type-assertion
      const tree = treeLayout(hierarchy).descendants() as PointNode[]
      const [initialX, initialY] = initialCoordinates

      tree.forEach((pointNode) => {
        pointNode.x = pointNode.x + initialX
        pointNode.y = pointNode.y - initialY
      })

      return tree
    },
    [initialCoordinates]
  )

  const build = useUnsafeCallback(
    (
      nextHeads: GraphQLEmployee[],
      nextEmployees: GraphQLEmployee[],
      nextUncollapsedIds: Set<string>
    ): PointNode<NodeDataTypes>[] => {
      if (nextEmployees.length === 0 || fetchingJobPositions) {
        return []
      }

      const buildData = (employee: GraphQLEmployee, isManagerHigher = false): EmployeeNodeData => {
        const id = employee.id
        const directReports = directReportsByManagerId.get(id) ?? []
        const hierarchyLength = descendantCountByEmployeeId.get(id) ?? 0
        const childrenLength = directReports.length
        const isUncollapsed = nextUncollapsedIds.has(id)

        if (!isUncollapsed) {
          return {
            employee,
            isUncollapsed,
            isSearchResult: false,
            hierarchyLength,
            children: [],
            childrenLength,
            type: 'employee',
          }
        }

        const employeeChildren = directReports.map((directReport) => {
          return buildData(directReport, currentEmployee?.id === employee.id)
        })
        const positionChildren = positionNodesByManagerId.get(id) ?? []

        const hasPermissions =
          canSeePositions && (currentEmployee?.id === id || isManagerHigher || canCreateJobPostings)
        const addPositionChildren: AddPositionNodeData[] =
          hasPermissions || canRequestUpsell
            ? [
                {
                  type: 'add-position',
                  managerId: id,
                  hasPermissions,
                  canRequestUpsell,
                },
              ]
            : []

        return {
          employee,
          isUncollapsed,
          isSearchResult: false,
          hierarchyLength,
          children: [...employeeChildren, ...positionChildren, ...addPositionChildren],
          childrenLength,
          type: 'employee',
        }
      }

      return buildTree({ children: nextHeads.map((head) => buildData(head)) })
    },
    [
      buildTree,
      canCreateJobPostings,
      canRequestUpsell,
      canSeePositions,
      currentEmployee?.id,
      descendantCountByEmployeeId,
      directReportsByManagerId,
      fetchingJobPositions,
      positionNodesByManagerId,
    ]
  )

  const tree = useUnsafeMemo<PointNode[]>(
    () => build(heads, employees, uncollapsedIds),
    [build, heads, employees, uncollapsedIds]
  )

  const findNodeByEmployeeId = useUnsafeCallback(
    (id: string): PointNode | void => findTreeNodeById(tree, id),
    [tree]
  )

  const traceTreePathFromEmployee = (employee: GraphQLEmployee): string[] => {
    let managerId = employee.managerId
    const idTreePath: string[] = []

    while (managerId) {
      const manager = employeeMap.get(managerId)

      if (!manager) {
        break
      }

      idTreePath.push(manager.id)
      managerId = manager.managerId
    }

    return idTreePath
  }

  return {
    tree,
    findNodeByEmployeeId,
    traceTreePathFromEmployee,
    refetch,
    initialCoordinates,
    build,
  }
}

export default useTree
